import { useCallback, useEffect, useRef } from "react";

/**
 * How close to the end of a scroll the next batch is asked for, in px —
 * roughly seven table rows, or three cards.
 *
 * The point of the number is that it is never reached: the next rows are laid
 * out while there is still a screenful above them, so the list goes on rather
 * than stopping to be continued.
 */
export const LOAD_AHEAD = 400;

export interface EndReached<T extends HTMLElement> {
  /** Goes on the element that scrolls. */
  ref: React.RefObject<T | null>;
  /** Goes on the same element's `onScroll`. */
  onScroll: () => void;
}

/**
 * Asks for more of a list as its end comes into view, for a scroll region that
 * grows rather than paging.
 *
 * `onEndReached` is called well before the last row — see `LOAD_AHEAD` — and
 * again while the rows still do not fill the box, so it has to be safe to call
 * more than once for the same batch. Callers hand back the next batch of rows
 * they are already holding; nothing here fetches.
 */
export function useEndReached<T extends HTMLElement>(
  hasMore: boolean,
  onEndReached?: () => void
): EndReached<T> {
  const ref = useRef<T>(null);

  // Read through a ref rather than closed over: callers build this as an
  // inline arrow, so depending on it would tear the effect below down and set
  // it up again on every render.
  const onEndReachedRef = useRef(onEndReached);
  useEffect(() => {
    onEndReachedRef.current = onEndReached;
  });

  // The batch that does not reach the bottom of the box, topped up until it
  // does. Without this a first batch shorter than the box would leave nothing
  // to scroll — and the scroll is the only thing that asks for more, so the
  // list would stop short on a tall screen with no way to go on.
  //
  // Every render, since what changes it is the height of the rows rather than
  // any value here. A box that has not been laid out yet — `clientHeight` of
  // zero, which is every box under the test runner — says nothing about
  // whether it is full, so it is left alone.
  useEffect(() => {
    const box = ref.current;
    if (!box || !hasMore) return;
    if (box.clientHeight > 0 && box.scrollHeight <= box.clientHeight) {
      onEndReachedRef.current?.();
    }
  });

  const onScroll = useCallback(() => {
    const box = ref.current;
    if (!box || !hasMore) return;
    const remaining = box.scrollHeight - box.scrollTop - box.clientHeight;
    if (remaining < LOAD_AHEAD) onEndReachedRef.current?.();
  }, [hasMore]);

  return { ref, onScroll };
}

export default useEndReached;
